import React from 'react';

export default function DonutChart({ title, data, size = 200 }) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  if (total === 0) {
    return (
      <div className="bg-white p-4 rounded shadow">
        {title && <h3 className="font-semibold mb-3 text-gray-800">{title}</h3>}
        <p className="text-center py-8 text-gray-400">No data</p>
      </div>
    );
  }

  const radius = size / 2;
  const strokeWidth = 45; // Thickness of the donut
  const center = size / 2;
  const circumference = 2 * Math.PI * (radius - strokeWidth / 2);
  let cumulativeOffset = 0;

  return (
    <div className="bg-white p-4 rounded shadow flex flex-col items-center">
      {title && <h3 className="font-semibold mb-3 text-gray-800 self-start">{title}</h3>}
      
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
          {data.map((item, index) => {
            const percentage = item.value / total;
            const strokeDasharray = `${percentage * circumference} ${circumference}`;
            const strokeDashoffset = -cumulativeOffset;
            cumulativeOffset += percentage * circumference;

            // Calculate mid angle to place the text inside the slice
            const startAngle = (cumulativeOffset - percentage * circumference) / circumference * 360;
            const endAngle = cumulativeOffset / circumference * 360;
            const midAngle = startAngle + (endAngle - startAngle) / 2;
            
            // Calculate X and Y for the text label
            const labelRadius = radius - strokeWidth / 2;
            const labelX = center + labelRadius * Math.cos((midAngle - 90) * (Math.PI / 180));
            const labelY = center + labelRadius * Math.sin((midAngle - 90) * (Math.PI / 180));

            return (
              <g key={index}>
                <circle
                  cx={center}
                  cy={center}
                  r={radius - strokeWidth / 2}
                  fill="transparent"
                  stroke={item.color}
                  strokeWidth={strokeWidth}
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="butt"
                />
                {/* 🔴 Show count inside the slice if it's large enough */}
                {percentage > 0.08 && (
                  <text
                    x={labelX}
                    y={labelY}
                    fill="white"
                    fontSize="14"
                    fontWeight="bold"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    transform={`rotate(90, ${labelX}, ${labelY})`} // Rotate text back to horizontal
                  >
                    {item.value}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        
        {/* Center Total Text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-2xl font-bold text-gray-800">{total}</span>
          <span className="text-xs text-gray-500">Total</span>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-col gap-2 mt-4 w-full">
        {data.map((item, index) => {
          const percentage = total > 0 ? Math.round((item.value / total) * 100) : 0;
          return (
            <div key={index} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></span>
                <span className="text-gray-700">{item.label}</span>
              </div>
              <span className="font-semibold text-gray-900">{item.value} ({percentage}%)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}